import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TransactionDto, TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import './Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const paymentAgentColumn: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionKey.reference,
    cell: (props: GridCellProps) => (
      <td
        className={generateClassNameForReverseType(props.dataItem)}
        style={{ color: 'var(--link)', textDecoration: 'underline' }}
      >
        <Link
          to={{
            pathname: generatePath('/transactions/:id', {
              id: props.dataItem.id,
            }),
          }}
        >
          {props.dataItem.referenceId}
        </Link>
      </td>
    ),
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Type',
    field: TransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      const type = props.dataItem?.type
      if (isEmpty(type)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{type.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Payment Agent',
    show: true,
    field: 'paymentAgent',
    children: [
      {
        title: 'Name',
        cell: (props: GridCellProps): JSX.Element => {
          const data: TransactionDto = props.dataItem
          if (isEmpty(props.dataItem.walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const paymentAgentTransaction = data.walletTransactions.find(
            (x) => x.paymentAgentTransaction
          )
          if (paymentAgentTransaction && paymentAgentTransaction.paymentAgentTransaction) {
            return (
              <td
                className={generateClassNameForReverseType(props.dataItem)}
                style={{ color: 'var(--link)', textDecoration: 'underline' }}
              >
                <Link
                  to={{
                    pathname: generatePath(links.recordsPaymentAgents.detailsUrl as string, {
                      id: paymentAgentTransaction.paymentAgentTransaction.paymentAgent.id,
                    }),
                  }}
                >
                  {paymentAgentTransaction.paymentAgentTransaction.paymentAgent.name}
                </Link>
              </td>
            )
          }

          const credit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Credit')?.wallet
          if (credit?.walletType?.id === 100) {
            return (
              <td
                className={generateClassNameForReverseType(props.dataItem)}
                style={{ color: 'var(--link)', textDecoration: 'underline' }}
              >
                <Link
                  state={{ redirectTo: links.transactionPaymentAgent.url }}
                  to={{
                    pathname: generatePath(links.recordsClients.detailsUrl as string, {
                      id: credit.client.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {credit.client.name}
                </Link>
              </td>
            )
          } else {
            const debit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Debit')?.wallet
            if (debit?.walletType?.id === 100) {
              return (
                <td
                  className={generateClassNameForReverseType(props.dataItem)}
                  style={{ color: 'var(--link)', textDecoration: 'underline' }}
                >
                  <Link
                    state={{ redirectTo: links.transactionPaymentAgent.url }}
                    to={{
                      pathname: generatePath(links.recordsClients.detailsUrl as string, {
                        id: debit.client.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {debit.client.name}
                  </Link>
                </td>
              )
            }
          }

          return <td className={generateClassNameForReverseType(props.dataItem)} />
        },
        filter: 'text',
      },
      {
        title: 'Wallet',
        cell: (props: GridCellProps): JSX.Element => {
          const data: TransactionDto = props.dataItem
          if (isEmpty(props.dataItem.walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const credit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Credit')?.wallet
          if (credit?.walletType?.id === 100) {
            return (
              <td
                className={generateClassNameForReverseType(props.dataItem)}
                style={{ color: 'var(--link)', textDecoration: 'underline' }}
              >
                <Link
                  state={{ redirectTo: links.transactionPaymentAgent.url }}
                  to={{
                    pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                      id: credit.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {credit.name}
                </Link>
              </td>
            )
          } else {
            const debit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Debit')?.wallet
            if (debit?.walletType?.id === 100) {
              return (
                <td
                  className={generateClassNameForReverseType(props.dataItem)}
                  style={{ color: 'var(--link)', textDecoration: 'underline' }}
                >
                  <Link
                    state={{ redirectTo: links.transactionPaymentAgent.url }}
                    to={{
                      pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                        id: debit.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {debit.name}
                  </Link>
                </td>
              )
            }
          }
          return <td />
        },
        filter: 'text',
      },
    ],
  },
  {
    title: 'Client',
    show: true,
    field: 'client',
    children: [
      {
        title: 'Name',
        cell: (props: GridCellProps): JSX.Element => {
          const data: TransactionDto = props.dataItem
          if (isEmpty(props.dataItem.walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const credit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Credit')?.wallet
          if (credit && credit.walletType?.id !== 100) {
            return (
              <td
                className={generateClassNameForReverseType(props.dataItem)}
                style={{ color: 'var(--link)', textDecoration: 'underline' }}
              >
                <Link
                  state={{ redirectTo: links.transactionPaymentAgent.url }}
                  to={{
                    pathname: generatePath(links.recordsClients.detailsUrl as string, {
                      id: credit.client.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {credit.client.name}
                </Link>
              </td>
            )
          } else {
            const debit = getCreditCreditOrFirstOneWallet(data.walletTransactions, 'Debit')?.wallet
            if (debit && debit.walletType?.id !== 100) {
              return (
                <td
                  className={generateClassNameForReverseType(props.dataItem)}
                  style={{ color: 'var(--link)', textDecoration: 'underline' }}
                >
                  <Link
                    state={{ redirectTo: links.transactionPaymentAgent.url }}
                    to={{
                      pathname: generatePath(links.recordsClients.detailsUrl as string, {
                        id: debit.client.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {debit.client.name}
                  </Link>
                </td>
              )
            }
          }
          return <td />
        },
        filter: 'text',
      },
      {
        title: 'Wallet',
        cell: (props: GridCellProps): JSX.Element => {
          if (isEmpty(props.dataItem.walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }
          if (isEmpty(props.dataItem.walletCreditTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }
          const walletCreditTransaction = props?.dataItem?.walletCreditTransactions[0]
          const credit = walletCreditTransaction?.wallet
          if (credit && credit.walletType?.id !== 100) {
            return (
              <td
                className={generateClassNameForReverseType(props.dataItem)}
                style={{ color: 'var(--link)', textDecoration: 'underline' }}
              >
                <Link
                  state={{ redirectTo: links.transactionPaymentAgent.url }}
                  to={{
                    pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                      id: credit.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {credit.name}
                </Link>
              </td>
            )
          } else {
            const [walletDebitTransaction] = props.dataItem.walletDebitTransactions
            const debit = walletDebitTransaction?.wallet
            if (debit && debit.walletType?.id !== 100) {
              return (
                <td
                  className={generateClassNameForReverseType(props.dataItem)}
                  style={{ color: 'var(--link)', textDecoration: 'underline' }}
                >
                  <Link
                    state={{ redirectTo: links.transactionPaymentAgent.url }}
                    to={{
                      pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                        id: debit.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {debit.name}
                  </Link>
                </td>
              )
            }
          }
          return <td />
        },
        resizable: true,
        filter: 'text',
      },
    ],
  },
  {
    title: 'Amount',
    field: TransactionKey.walletTransactionAmount,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem?.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }

      const [walletTransaction] = walletTransactions || []
      const amount = walletTransaction?.amount
      if (isEmpty(amount)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Currency',
    field: TransactionKey.walletTransactionWalletCurrency,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem?.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const [walletTransaction] = walletTransactions || []
      const currencyId = walletTransaction?.wallet?.currency?.id
      if (isEmpty(currencyId)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem?.state
      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TransactionKey.walletTransactionWalletCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem?.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const [walletCreditTransaction] = props.dataItem.walletCreditTransactions || []
      const company = walletCreditTransaction?.wallet?.company
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    filter: 'text',
  },
]

export default paymentAgentColumn
